import { isEmpty } from 'lodash'
import { useLocation } from 'react-router-dom'

import { HOME_PATHS } from 'components/home/HomeLayoutWrapper'
import { useGetLargeScaleTestPermissions } from 'components/large-scale/hooks/use-get-large-scale-test-permissions'
import { LARGE_SCALE_URLS } from 'components/large-scale/utils/large-scale-routes'
import { useWhatRanAccess } from 'components/what-ran/use-what-ran-access'
import useAuth from 'hooks/use-auth'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { useManageSharedAssets } from 'hooks/use-manage-shared-assets'
import { trans } from 'i18n/translate'
import { hasSupportAppPermission } from 'support/helpers/support-rbac'
import {
  attacksActiveGet,
  failureFlagsExperimentsActiveGet,
  orgsIdGet,
  reliabilityTestsActiveGet,
  scenariosActiveGet,
  schedulesAttacksPagedGet,
  schedulesScenariosPagedGet,
  schedulesServicesPagedGet,
  servicesGet,
  testSuitesGet
} from 'utils/endpoints-access'

import type { FontAwesomeIconName, LegacyIconName } from 'components/Icon'
import type { CSSProperties } from 'react'

export type SubNavItemConfig = {
  disabled?: boolean
  enabled: boolean
  name: string
  route: string
  text: string
  icon?: FontAwesomeIconName | LegacyIconName
  showNotification?: boolean
  state?: Record<string, any>
}

export interface NavItemConfig {
  isActive: boolean
  name: string
  disabled: boolean
  icon: FontAwesomeIconName | LegacyIconName
  text: string
  route?: string
  state?: Record<string, any>
  props?: Record<string, any>
  showBadgeCount?: boolean
  showNotification?: boolean
  enabled: boolean
  showSeparator?: boolean
  style?: CSSProperties
  subnav?: SubNavItemConfig[]
}

export interface UseGremlinNavigationHook {
  navigationConfigItems: NavItemConfig[]
}

export function useGremlinNavigation({
  userHasTeams
}: {
  userHasTeams: boolean
}): UseGremlinNavigationHook {
  const {
    largeScaleEvents: isLargeScaleEventsEnabled,
    qbrDashboards: isQbrDashboardsEnabled
  } = useFeatureFlags()
  const { pathname: currentPathname } = useLocation()
  const {
    currentTeamId,
    hasEndpointAccess,
    hasFaultInjection,
    hasFailureFlags,
    hasReliabilityManagement,
    session
  } = useAuth()

  const { pendingIncomingRequestsByAssetId } = useManageSharedAssets()

  const hasPendingClusterAccessRequests = !isEmpty(
    pendingIncomingRequestsByAssetId
  )

  const whatRanAccess = useWhatRanAccess()
  const { canReadLargeScaleTests, canReadLargeScaleTestReports } =
    useGetLargeScaleTestPermissions()

  const disableForOnboarding =
    currentPathname.startsWith('/onboarding') &&
    currentPathname !== '/onboarding/profile'

  const isOnboardingOrHasNoTeams = disableForOnboarding || !userHasTeams

  const accessOptions = { teamId: currentTeamId }

  const canShowNowRunning =
    hasEndpointAccess(attacksActiveGet, accessOptions) ||
    hasEndpointAccess(scenariosActiveGet, accessOptions) ||
    hasEndpointAccess(reliabilityTestsActiveGet, accessOptions) ||
    hasEndpointAccess(failureFlagsExperimentsActiveGet, accessOptions)

  const canShowServices =
    hasReliabilityManagement && hasEndpointAccess(servicesGet, accessOptions)

  const canShowWhatsScheduled =
    hasEndpointAccess(schedulesAttacksPagedGet, accessOptions) ||
    hasEndpointAccess(schedulesScenariosPagedGet, accessOptions) ||
    hasEndpointAccess(schedulesServicesPagedGet, accessOptions)

  const canShowWhatRan = whatRanAccess.section

  const canShowLargeScale =
    isLargeScaleEventsEnabled &&
    canReadLargeScaleTests &&
    canReadLargeScaleTestReports

  const navigationConfigItems: NavItemConfig[] = [
    {
      isActive:
        currentPathname.startsWith(HOME_PATHS.TeamReliability) ||
        currentPathname.startsWith(HOME_PATHS.GettingStarted),
      name: 'home',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'house',
      text: 'Home',
      route: HOME_PATHS.TeamReliability,
      props: { 'data-test-nav-home': true },
      enabled: hasReliabilityManagement && isQbrDashboardsEnabled
    },
    {
      isActive: currentPathname.startsWith('/reports'),
      name: 'reports',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartLineUp',
      text: 'Reports',
      props: { 'data-test-nav-company-dashboard': true },
      enabled: hasReliabilityManagement,
      subnav: [
        {
          enabled: true,
          name: 'reliability-report',
          text: 'Reliability Report',
          route: '/reports/company'
        },
        {
          enabled: true,
          name: 'team-risk',
          text: 'Team Risk',
          route: '/reports/team-risk'
        },
        {
          enabled: true,
          name: 'team-score',
          text: 'Team Score',
          route: '/reports/team-score'
        }
      ]
    },
    {
      isActive: currentPathname.startsWith('/now-running'),
      name: 'now-running',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'regularArrowsRotate',
      text: 'Now Running',
      route: '/now-running',
      showBadgeCount: true,
      props: { 'data-test-nav-now-running': true },
      enabled: canShowNowRunning
    },
    {
      isActive: currentPathname.startsWith('/schedules'),
      name: 'whats-scheduled',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'calendar',
      text: "What's Scheduled",
      route: '/schedules',
      props: { 'data-test-nav-scheduled-new': true },
      enabled: canShowWhatsScheduled
    },
    {
      isActive: currentPathname.startsWith('/run-history'),
      name: 'run-history',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'rectangleHistory',
      text: 'What Ran',
      route: '/run-history',
      props: { 'data-testid': 'nav-run-history' },
      enabled: canShowWhatRan
    },
    {
      isActive: currentPathname.startsWith('/getting-started'),
      name: 'getting-started',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'graduationCap',
      text: 'Getting Started',
      route: '/getting-started',
      props: { 'data-test-nav-getting-started': true },
      enabled: hasReliabilityManagement && !isQbrDashboardsEnabled
    },
    {
      isActive: currentPathname.startsWith('/services'),
      name: 'services',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartNetwork',
      text: 'Services',
      route: '/services',
      state: {
        clearState: true
      },
      props: { 'data-test-nav-services': true },
      enabled: canShowServices
    },
    {
      isActive:
        currentPathname.startsWith('/attacks') ||
        currentPathname.startsWith('/alfi'),
      name: 'attacks',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'flask',
      text: 'Experiments',
      route: '/attacks',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/scenarios'),
      name: 'scenarios',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'clipboardList',
      text: 'Scenarios',
      route: '/scenarios/my-scenarios',
      state: {
        clearSearchState: true
      },
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/failure-flags'),
      name: 'failure-flags',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'flag',
      text: trans('failure_flags.label.plural'),
      route: '/failure-flags/list',
      props: { 'data-test-nav-failure-flags': true },
      enabled: hasFailureFlags
    },
    {
      isActive: currentPathname.startsWith(LARGE_SCALE_URLS.base()),
      name: 'large-scale-tests',
      disabled: !canShowLargeScale || isOnboardingOrHasNoTeams,
      icon: 'cubes',
      text: 'Large Scale',
      route: LARGE_SCALE_URLS.base(),
      enabled: canShowLargeScale && hasReliabilityManagement
    },
    {
      isActive: currentPathname.startsWith('/gamedays'),
      name: 'gamedays',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'joystick',
      text: 'GameDays',
      route: '/gamedays',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/configurations'),
      name: 'configurations',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'sliders',
      text: 'Configurations',
      showNotification: hasPendingClusterAccessRequests,
      enabled: true,
      subnav: [
        {
          enabled: hasReliabilityManagement && hasEndpointAccess(testSuitesGet),
          name: 'test-suites',
          text: 'Test Suites',
          route: '/configurations/test-suites',
          icon: 'books'
        },
        {
          enabled: true,
          name: 'status-checks',
          text: 'Health Checks',
          route: '/configurations/health-checks/list',
          icon: 'wavePulse'
        },
        {
          enabled: hasEndpointAccess(orgsIdGet),
          name: 'agents',
          text: 'Agents',
          route: '/configurations/agents',
          icon: 'clients'
        },
        {
          enabled: true,
          name: 'clusters',
          text: 'Clusters',
          route: '/configurations/clusters',
          icon: 'dharmachakra',
          showNotification: hasPendingClusterAccessRequests
        },
        {
          enabled: true,
          name: 'pni',
          text: 'PNI',
          route: '/configurations/private-network-integrations',
          icon: 'lock'
        }
      ]
    },
    {
      isActive: currentPathname === '/help',
      name: 'help',
      disabled: false,
      icon: 'messageQuestion',
      text: 'Help',
      route: '/help',
      enabled: true,
      showSeparator: true
    },
    {
      isActive:
        currentPathname.startsWith('/support/') ||
        currentPathname.startsWith('/supportnew/'),
      name: 'support',
      disabled: false,
      icon: 'support',
      text: 'Support App',
      route: '/support',
      enabled: hasSupportAppPermission(session)
    }
  ]

  return {
    navigationConfigItems
  }
}
